export const OPERATOR_REFERENCE_TTL_MS = 60_000;
export const OPERATOR_AUXILIARY_TTL_MS = 30_000;

type RefreshRequest = {
  id: number;
  scope: string;
  loadReference: boolean;
  loadAuxiliary: boolean;
};

type RefreshOptions = {
  scope: string;
  operator: boolean;
  background: boolean;
  forceReference: boolean;
  now: number;
};

/**
 * The operator ledger has a shorter operational deadline than reference data.
 * Start both reads together, but publish the ledger as soon as it is verified.
 */
export async function resolveRunsBeforeReference<R, T>(
  runs: Promise<R>,
  reference: Promise<T>,
  onRuns: (value: R) => void,
) {
  const resolvedRuns = await runs;
  onRuns(resolvedRuns);
  return { runs: resolvedRuns, reference: await reference };
}

/** Coordinates polling only; the caller remains responsible for HTTP cancellation and response ownership. */
export class ProductionDataRefreshCoordinator {
  private reference: { scope: string; loadedAt: number } | null = null;
  private auxiliary: { scope: string; loadedAt: number } | null = null;
  private nextRequestId = 0;
  private readonly inFlightByScope = new Map<string, Set<number>>();
  private readonly auxiliaryInFlightScopes = new Set<string>();

  start(options: RefreshOptions): RefreshRequest | null {
    // A poll must not supersede an active manual/mutation refresh for this scope.
    // Forced requests deliberately may supersede a poll; response ownership remains in the hook.
    if (options.background && this.inFlightByScope.get(options.scope)?.size) return null;
    const id = ++this.nextRequestId;
    const inFlight = this.inFlightByScope.get(options.scope) ?? new Set<number>();
    inFlight.add(id);
    this.inFlightByScope.set(options.scope, inFlight);
    const referenceExpired = this.reference === null || this.reference.scope !== options.scope ||
      options.now - this.reference.loadedAt >= OPERATOR_REFERENCE_TTL_MS;
    const auxiliaryExpired = this.auxiliary === null || this.auxiliary.scope !== options.scope ||
      options.now - this.auxiliary.loadedAt >= OPERATOR_AUXILIARY_TTL_MS;
    return {
      id,
      scope: options.scope,
      loadReference: options.forceReference || referenceExpired,
      // Reports and notifications are useful but do not decide whether the
      // operator's confirmed ledger can render. Avoid requesting them with
      // every five-second counter refresh.
      loadAuxiliary: !this.auxiliaryInFlightScopes.has(options.scope) && (!options.background || auxiliaryExpired),
    };
  }

  finish(request: RefreshRequest, referenceLoaded: boolean, now: number, auxiliaryLoaded = false) {
    const inFlight = this.inFlightByScope.get(request.scope);
    inFlight?.delete(request.id);
    if (inFlight?.size === 0) this.inFlightByScope.delete(request.scope);
    if (referenceLoaded) this.reference = { scope: request.scope, loadedAt: now };
    if (auxiliaryLoaded) this.auxiliary = { scope: request.scope, loadedAt: now };
  }

  startAuxiliary(scope: string) {
    if (this.auxiliaryInFlightScopes.has(scope)) return false;
    this.auxiliaryInFlightScopes.add(scope);
    return true;
  }

  finishAuxiliary(scope: string, loaded: boolean, now: number) {
    this.auxiliaryInFlightScopes.delete(scope);
    if (loaded) this.auxiliary = { scope, loadedAt: now };
  }
}
